import first from 'lodash/first';
import startCase from 'lodash/startCase';
import upperCase from 'lodash/upperCase';

import {
  FIRST_NAME,
  FIRST_NAME_AND_INITIAL,
  TITLE_AND_INITIAL,
  TITLE_AND_SURNAME,
  CURRICULUM_OPTIONS_EDIT,
  WA,
} from 'global/constants';
import { InquisitiveCurriculum } from 'global/graphql/scalars.type';

type School = {
  name: string;
  suburb: string;
  postcode: string;
  state: string;
};

export const isExternalLink = (link: string): boolean => {
  return (
    link.startsWith('http://') ||
    link.startsWith('https://') ||
    link.startsWith('mailto:') ||
    link.startsWith('tel:')
  );
};

export const capitalizeDisplayName = (name: string) => {
  if (name.search('-') > 0) {
    return startCase(name).replace(/\s/g, '-');
  }
  return startCase(name);
};

export const formatSchoolName = ({
  school,
  isPreselectedSchool,
}: {
  school: School;
  isPreselectedSchool?: boolean;
}) => {
  if (isPreselectedSchool) {
    return `${school.name.replace('-', 'in')}`;
  }
  return `${school.name}, ${school.suburb}, ${school.postcode}, ${school.state}`;
};

export const truncateAtWordBoundary = (str: string, maxLength: number) => {
  return maxLength > str.length
    ? str
    : str.substring(0, str.lastIndexOf(' ', maxLength));
};

export const formatUserName = (
  user: {
    title?: string | null;
    displayName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  },
  displayNameFormat: string | null,
  returnExistingDisplayName = true
) => {
  if (user.displayName && returnExistingDisplayName) {
    return user.displayName;
  }
  switch (displayNameFormat) {
    case FIRST_NAME:
      return startCase(user.firstName || undefined);
    case FIRST_NAME_AND_INITIAL:
      return `${startCase(
        user.firstName || undefined
      )} ${user.lastName?.[0].toUpperCase()}`;
    case TITLE_AND_INITIAL:
      if (user.title && user.lastName)
        return `${user.title} ${user.lastName[0].toUpperCase()}`;
      return '';
    case TITLE_AND_SURNAME:
      if (user.title && user.lastName)
        return `${user.title} ${capitalizeDisplayName(user.lastName)}`;
      return '';

    default:
      // With school teacher firstName and lastName can be null
      return `${upperCase(first(user.firstName))}${upperCase(
        first(user.lastName)
      )}`;
  }
};

// slug looks like 64-important-places
export const getIdFromSlug = (slug: string): number | undefined => {
  return parseInt(slug.split('-')[0], 10) || undefined;
};

export const formatCurriculum = (curriculums: InquisitiveCurriculum[]) => {
  if (curriculums.length === 1 && curriculums[0] === WA) {
    return 'WA/AU';
  }

  return curriculums.length === CURRICULUM_OPTIONS_EDIT.length
    ? 'All Curriculums'
    : curriculums
        .slice()
        .sort()
        .join(', ');
};

export const getDomain = (url: string): string => {
  var match = url.match(/:\/\/(www[0-9]?\.)?(.[^/:]+)/i);
  if (
    match != null &&
    match.length > 2 &&
    typeof match[2] === 'string' &&
    match[2].length > 0
  ) {
    return match[2];
  } else {
    return url;
  }
};

export const removeUrlProtocol = (url: string): string => {
  return url.replace(/^https?:\/\//, '');
};

export const COMMA_KEYCODE = 188;
export const ENTER_KEYCODE = 13;
export const ENTER_KEY = 'Enter';

export const isString = (variable: any): boolean => {
  return typeof variable === 'string' || variable instanceof String;
};

export const trimTrailingFullstop = (sourceString: string) => {
  const trimmed = sourceString.trim();

  if (trimmed.endsWith('.')) {
    return trimmed.slice(0, -1);
  }
  return trimmed;
};

export function joinWithAnd(stringList: string[]): string {
  if (stringList.length <= 1) return stringList[0] || '';
  const list = stringList.slice(0, stringList.length - 1);
  const last = stringList[stringList.length - 1];
  return `${list.join(', ')} and ${last}`;
}

export function getPathAndSearch(urlString: string): string {
  let url: URL;
  try {
    url = new URL(urlString);
  } catch (e) {
    return '';
  }

  return `${url.pathname}${url.search}`;
}
